import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { authClient } from "../lib/authApi";
import { Pen, Warning } from "@phosphor-icons/react";
import { useAuth } from "../auth/AuthContext";
import { ReasonPicker, computeValidity } from "./ReasonPicker";
import { InfoHint } from "./InfoHint";
import { SIGNOFF_PRESETS } from "./reasonPresets";
import { BlockingMessageDialog } from "./BlockingMessageDialog";
import { PresetReasonSelect } from "./PresetReasonSelect";
import { reconOverridePresets } from "../lib/reviewReasonPresets";
import { useIncludeHeldRecalc } from "./useIncludeHeldRecalc";
import { isSignedOff } from "../workflow/calcStatus";   // A2-581 lead 35

const MIN_REASON_LEN = 3;
// v0.6.12 A5-27 — the reconciliation-override reason is printed on the
// export's Filing Status tab, yet it shared the 3-character floor of the
// per-row ack reasons above, the weakest gate on any audit reason in the
// app. A typed one now needs 20, the drop-panel level (DROP_REASONS); the
// presets all clear it. Frontend only: the backend still asks for
// non-empty (models/signoff.py), unchanged.
const OVERRIDE_MIN_LEN = 20;
const todayIso = () => new Date().toISOString().slice(0, 10);

export const SignoffPanel = ({ calc, onSigned, onRecalculated, signoffDisabled, signoffTooltip, step6Blockers, onAdvance }) => {
  const { t } = useTranslation();
  const { admin } = useAuth();
  const operatorName =
    admin?.display_name || admin?.email?.split("@")[0] || t("ui.signoff.operator_unknown");
  const [acks, setAcks] = useState({});
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showSignoffBlockedDialog, setShowSignoffBlockedDialog] = useState(false);
  // v0.6.10 — the reconciliation gate (api/_recon_gate.py) refuses a
  // sign-off whose R1/R3/R4/R5 arithmetic failed. It is unblockable,
  // but only by recording WHY, which then prints on the export.
  const [reconBlock, setReconBlock] = useState(null);
  const [overrideReason, setOverrideReason] = useState("");
  const [overridePreset, setOverridePreset] = useState("");
  // A2-479 — acknowledged held EU sales are added by a re-run; wait for it
  const [recalc, setRecalc] = useState(null);
  useIncludeHeldRecalc({
    jobId: recalc?.job_id,
    onResult: (result) => { setRecalc(null); onRecalculated?.(result); },
    onError: (msg) => { setRecalc(null); setError(t("ui.signoff.recalc_failed", { msg, defaultValue: `The recalculation did not finish (${msg}). Run the calculation again from the Calculate step.` })); },
  });

  const pending = calc?.pending_txn_ids || [];
  const defaultReasons = calc?.pending_reasons || {};
  const signed = isSignedOff(calc);
  const frozen = !!calc?.frozen;

  // v0.4.40 RQ3 — initialise input state with the server-suggested
  // default reason for each pending id. Operator can overwrite.
  useEffect(() => {
    if (pending.length === 0) return;
    setAcks((prev) => {
      const next = { ...prev };
      for (const tid of pending) {
        if (next[tid] === undefined) next[tid] = defaultReasons[tid] || "";
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calc?.id, pending.length]);

  const acceptAllDefaults = () => {
    setAcks((prev) => {
      const next = { ...prev };
      for (const tid of pending) {
        const empty = !((next[tid] || "").trim().length >= MIN_REASON_LEN);
        if (empty && defaultReasons[tid]) next[tid] = defaultReasons[tid];
      }
      return next;
    });
  };

  const reasonValid = (tid) => {
    const v = computeValidity(acks[tid] || "", MIN_REASON_LEN);
    return v.lengthOk && v.placeholdersOk;
  };

  const allAcksValid = useMemo(
    () => pending.every(reasonValid),
    [pending, acks]   // eslint-disable-line react-hooks/exhaustive-deps
  );
  // A2-582 (independent audit, 2026-10-27) — ask the server what would stop
  // sign-off (api/_signoff_readiness: the same checks the sign-off route
  // raises), and say so before the button is pressed instead of after.
  const [readiness, setReadiness] = useState(null);
  useEffect(() => {
    if (!calc?.id || signed || frozen) { setReadiness(null); return undefined; }
    let live = true;
    Promise.resolve().then(() => authClient.get(`/runs/calc/${calc.id}/signoff-readiness`))
      .then((r) => { if (live) setReadiness(r?.data || null); })
      .catch(() => { if (live) setReadiness(null); });   // the route still refuses with its reason
    return () => { live = false; };
  }, [calc?.id, signed, frozen, error]);
  const blockers = readiness?.blockers || [];
  const canSign = !!admin && allAcksValid && blockers.length === 0;

  if (!calc) return null;

  const sign = async ({ override } = {}) => {
    setError(null);
    // A1-93 — EVERY sign path (incl. "Sign off anyway" after a failed
    // reconciliation) goes through the step gate, not only the main button:
    // a calc that is out of date or still has open review rows is not signed.
    if (signoffDisabled) { setShowSignoffBlockedDialog(true); return; }
    if (!canSign || recalc) return;   // A2-479: one re-run at a time
    setBusy(true);
    try {
      const { data } = await authClient.post(
        `/runs/calc/${calc.id}/signoff`,
        {
          operator_name: operatorName.trim(),
          acknowledged_pending: pending.map((tid) => ({
            txn_id: tid,
            reason: acks[tid].trim(),
            acknowledged_by: operatorName.trim(),
          })),
          ...(override ? { reconciliation_override: { reason: override } } : {}),
        }
      );
      setReconBlock(null);
      onSigned?.(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Not an error to retype past — it names the failing checks and
      // takes a written reason to proceed. Surface it as its own step.
      if (detail?.error === "RECONCILIATION_FAILED") {
        setReconBlock(detail);
        return;
      }
      if (detail?.error === "RECALCULATING_TO_INCLUDE_SALES") {
        setRecalc(detail);
        return;
      }
      setError(
        detail?.message || JSON.stringify(detail) || e.message
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <section
      data-testid="signoff-panel"
      className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
    >
      <div className="border-b border-slate-200 dark:border-slate-700 px-4 py-2 flex items-center justify-between">
        <div className="text-xs tracking-normal text-slate-500 dark:text-slate-400 flex items-center gap-2">
          <Pen size={14} /> {t("ui.signoff.section_title")}
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400">
          {frozen ? t("ui.signoff.frozen") : signed ? t("ui.signoff.signed") : t("ui.signoff.unsigned")}
        </div>
      </div>

      {recalc && (
        <div data-testid="signoff-recalculating" role="status" aria-live="polite"
             className="px-4 py-3 text-sm bg-sky-50 dark:bg-sky-950/40 text-sky-900 dark:text-sky-100 border-b border-sky-200 dark:border-sky-800">
          {/* A2-493 — a UK seller's accepted sales are exports (UK Box 6), not intra-EU sales */}
          {!recalc.uk_count ? t("ui.signoff.recalculating", { count: recalc.count,
            defaultValue: `Recalculating to add the ${recalc.count} acknowledged EU sale(s) to the return as intra-EU sales… You will sign the new calculation when it is ready.` })
            : recalc.uk_count === recalc.count ? t("ui.signoff.recalculating_uk", { count: recalc.count,
            defaultValue: `Recalculating to add the ${recalc.count} acknowledged sale(s) to the return as sales outside the scope of UK VAT (UK Box 6)… You will sign the new calculation when it is ready.` })
            : t("ui.signoff.recalculating_mixed", { count: recalc.count,
            defaultValue: `Recalculating to add the ${recalc.count} acknowledged sale(s) to the return: EU sales as intra-EU sales, UK sales as outside the scope of UK VAT (UK Box 6)… You will sign the new calculation when it is ready.` })}
        </div>
      )}
      {frozen ? (
        <div
          data-testid="filing-reference-banner"
          className="px-4 py-4 bg-emerald-600/10 dark:bg-emerald-900/40 border-t border-emerald-500/30"
        >
          <div className="text-xs tracking-normal text-emerald-700 dark:text-emerald-300 mb-1">
            {t("ui.signoff.filing_reference_title")}
          </div>
          <div
            className="tabular-nums text-xl text-emerald-700 dark:text-emerald-300"
            data-testid="filing-reference"
          >
            {calc.filing_reference}
          </div>
          <div className="text-xs text-slate-600 dark:text-slate-300 mt-1">
            {t("ui.signoff.filing_reference_meta", { operator: calc.signed_off_by, when: calc.frozen_at })}
          </div>
        </div>
      ) : (
        <div className="px-4 py-4 space-y-3">
          <div className="grid grid-cols-12 gap-3 items-end">
            <div className="col-span-12 md:col-span-7">
              <label className="text-[10px] tracking-normal text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1">
                {t("ui.signoff.operator_label")}
                <InfoHint testid="operator-info" label={t("ui.signoff.operator_helper")} />
              </label>
              <div
                data-testid="operator-name-input"
                className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-50 px-3 py-2 text-sm rounded-xl flex items-center justify-between"
              >
                <span data-testid="operator-name-value">{operatorName}</span>
                <span className="text-[10px] tracking-normal text-teal-700/80 dark:text-teal-300">
                  {admin?.role || t("ui.signoff.operator_role_unknown")}
                </span>
              </div>
            </div>
            <div className="col-span-12 md:col-span-5 text-xs text-slate-500 dark:text-slate-400 text-right">
              {t("ui.signoff.pending_label")}&nbsp;
              <span
                data-testid="pending-count"
                className={pending.length > 0 ? "text-teal-700 dark:text-teal-300" : "text-slate-500 dark:text-slate-400"}
              >
                {pending.length}
              </span>
            </div>
          </div>

          {pending.length > 0 && (
            <div
              data-testid="pending-ack-list"
              className="border border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/40 p-3 space-y-2"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs tracking-normal text-teal-700 dark:text-teal-300 flex items-center gap-2">
                  <Warning size={12} /> {t("ui.signoff.ack_block_title", { min: MIN_REASON_LEN })}
                </div>
                <button
                  type="button"
                  data-testid="signoff-accept-all-defaults"
                  onClick={acceptAllDefaults}
                  className="text-[10px] tracking-normal text-teal-700 dark:text-teal-300 hover:text-teal-100 border border-teal-500/50 hover:border-teal-300 dark:border-teal-700 rounded-lg px-2 py-1"
                >
                  {t("ui.signoff.accept_all_defaults", "Accept all default reasons")}
                </button>
              </div>
              {pending.map((tid) => {
                const valid = reasonValid(tid);
                return (
                  <div key={tid} className="grid grid-cols-12 gap-2 items-start">
                    <span className="col-span-3 tabular-nums text-xs text-slate-600 dark:text-slate-300 truncate pt-1.5">
                      {tid.slice(0, 12)}…
                    </span>
                    <div className="col-span-9">
                      {/* v0.5.0c — preset dropdown + textarea via
                          ReasonPicker. Replaces the bare `<input>` so
                          operators no longer face a blank field. The
                          picker enforces both the MIN_REASON_LEN gate
                          and the `<TOKEN>` placeholder fail-loud rule. */}
                      <ReasonPicker
                        testid={`ack-reason-${tid}`}
                        value={acks[tid] || ""}
                        onChange={(v) =>
                          setAcks((p) => ({ ...p, [tid]: v }))
                        }
                        presets={SIGNOFF_PRESETS}
                        minChars={MIN_REASON_LEN}
                        placeholder={t("ui.signoff.ack_placeholder")}
                        // v0.6 — "standard" and "with-accountant" carry
                        // <DATE> unfilled, blocking Save until the
                        // operator noticed and hand-edited it (same bug
                        // class the v0.5.0f+4 bulk-ack fix addressed
                        // elsewhere). <NAME> on with-accountant stays —
                        // not known to the system.
                        onPresetPicked={(p) => {
                          // A2-430: "with-accountant" names the date of a
                          // meeting with the accountant — picked, not today.
                          if (p.id === "standard") {
                            setAcks((prev) => ({
                              ...prev,
                              [tid]: (p.template || "").replace("<DATE>", todayIso()),
                            }));
                          }
                        }}
                      />
                      {!valid && (
                        <div
                          data-testid={`ack-reason-helper-${tid}`}
                          className="text-[10px] text-red-700 dark:text-red-300 mt-0.5"
                        >
                          {t("ui.signoff.ack_reason_required", { min: MIN_REASON_LEN })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {reconBlock && (
            <div
              data-testid="signoff-recon-block"
              className="rounded border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/40 p-3 space-y-2"
            >
              <div className="flex items-center gap-1 text-red-800 dark:text-red-200 text-xs font-medium">
                <Warning size={12} />
                {t("ui.signoff.recon_block_title",
                   "These figures do not add up")}
              </div>
              <div
                data-testid="signoff-recon-rules"
                className="tabular-nums text-[11px] text-red-900 dark:text-red-100"
              >
                {reconBlock.failure_count}
                {" · "}
                {(reconBlock.rules || []).join(", ")}
              </div>
              {/* A2-536 — the differences themselves, so the reason given fits them */}
              <ul data-testid="signoff-recon-diffs" className="tabular-nums text-xs text-red-900 dark:text-red-100 space-y-0.5">
                {(reconBlock.failures || []).map((f, i) => (
                  <li key={i}>{f.label}: {t("ui.signoff.recon_diff", { delta: f.delta, defaultValue: `difference ${f.delta}` })}</li>
                ))}
              </ul>
              <div className="text-xs text-red-800 dark:text-red-200">
                {t("ui.signoff.recon_block_body",
                   "Reconciliation checks failed on this calculation. " +
                   "Correct the source data and run it again — or, if you " +
                   "have checked these calc reconciliation failures and it " +
                   "is safe to file, say why.")}
              </div>
              <PresetReasonSelect
                testid="signoff-override-reason-preset"
                presets={reconOverridePresets(reconBlock.failures)}
                preset={overridePreset}
                onPick={(p, v) => { setOverridePreset(p); setOverrideReason(v); }}
              />
              <div className="text-[11px] text-red-700 dark:text-red-300">
                {t("ui.signoff.recon_override_note",
                   "This reason is recorded against the filing and printed " +
                   "on the Filing Status tab of the export.")}
              </div>
              <button
                type="button"
                data-testid="signoff-override-button"
                disabled={busy || overrideReason.trim().length < OVERRIDE_MIN_LEN}
                onClick={() => sign({ override: overrideReason.trim() })}
                className="text-[10px] tracking-normal text-red-700 dark:text-red-300 hover:text-red-100 border border-red-500/50 hover:border-red-300 dark:border-red-700 rounded-lg px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {t("ui.signoff.recon_override_button",
                   "Sign off anyway, and record this reason")}
              </button>
            </div>
          )}
          {error && (
            <div data-testid="signoff-error" className="text-red-700 dark:text-red-300 text-xs">
              {error}
            </div>
          )}

          {!signed && blockers.length > 0 && (
            <div data-testid="signoff-blockers" role="status"
                 className="text-xs text-slate-800 dark:text-slate-100 border border-orange-300 dark:border-orange-800 rounded-lg p-3 space-y-1">
              <div className="font-semibold">
                {t("ui.signoff.blocked_title", { defaultValue: "Sign-off is not possible yet:" })}
              </div>
              <ul className="list-disc pl-5 space-y-0.5">
                {blockers.map((b) => (
                  <li key={b.error} data-testid={`signoff-blocker-${b.error}`}>{b.message}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex gap-3 items-center">
            <button
              type="button"
              data-testid="signoff-button"
              data-resolution-class="self-resolve"
              data-resolution-endpoint="POST /api/runs/calc/{calc_id}/signoff"
              // A2-581 lead 3: natively disabled only when the step itself
              // is not what blocks it - otherwise the click that shows the
              // step's reason (below) could never fire
              disabled={signed || busy || !!recalc || (!canSign && !signoffDisabled)}
              aria-disabled={signoffDisabled}
              title={signoffDisabled ? signoffTooltip : undefined}
              onClick={() => {
                // v0.6.8 — signoffDisabled's reason used to be hover-
                // only (title=); a compliance-relevant action's block
                // reason deserves more than a tooltip. !canSign already
                // has its own always-visible inline reason below.
                if (signoffDisabled) { setShowSignoffBlockedDialog(true); return; }
                sign();
              }}
              className="bg-teal-600 hover:bg-teal-700 disabled:opacity-30 disabled:cursor-not-allowed aria-disabled:opacity-30 aria-disabled:cursor-not-allowed text-white font-semibold px-5 py-2 text-xs rounded-xl"
            >
              {signed ? t("ui.signoff.signed") : t("ui.signoff.sign_off")}
            </button>
            {signed && (
              <div>
                {step6Blockers?.length > 0 ? (
                  // v0.6.8 fix — signed off but Step 6 is still locked for a
                  // reason other than sign-off itself (e.g. an unresolved
                  // review flag). Show that reason here, not a "continue"
                  // hint that implies nothing is left to do — see
                  // workflowState.js's step6Reasons comment for the report
                  // this was missing on.
                  <div
                    data-testid="signoff-step6-blocker"
                    className="text-[10px] text-amber-700 dark:text-amber-300"
                  >
                    {t("ui.signoff.step6_locked", { reason: step6Blockers[0], defaultValue: `⚠ Step 6 is still locked — ${step6Blockers[0]}` })}
                  </div>
                ) : (
                  // v0.6.8 fix — this used to be static text telling the
                  // operator to "continue via the stepper", but the
                  // Stepper never actually unlocks Step 6's tab on its
                  // own (see the onSigned comment in Step5Signoff.jsx) —
                  // a dead end for anyone who reloads or lands on this
                  // route after already signing off. Make it a real
                  // button when there's nothing left blocking.
                  <button
                    type="button"
                    data-testid="signoff-continue-to-freeze"
                    onClick={() => onAdvance?.()}
                    className="text-[10px] text-teal-700 dark:text-teal-300 underline decoration-dotted hover:text-teal-600 dark:hover:text-teal-200"
                  >
                    {t(
                      "ui.signoff.freeze_hint",
                      "Continue to Step 6 to review the freeze disclosures and lock for filing.",
                    )}
                  </button>
                )}
              </div>
            )}
            {/* A2-581 lead 3: "Fill in every review note" showed for every
                cause, including a server blocker (listed above) or an
                out-of-date calc. The note says the real one. */}
            {!signed && (signoffDisabled || !allAcksValid) && (
              <div
                data-testid="signoff-disabled-reason"
                className="text-[10px] text-slate-500 dark:text-slate-400"
              >
                {signoffDisabled ? signoffTooltip : t("ui.signoff.disabled_no_reasons")}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
    <BlockingMessageDialog
      open={showSignoffBlockedDialog} testid="signoff-blocked-dialog" severity="warning"
      title={t("ui.signoff.blocked_dialog_title", { defaultValue: "Can't sign off yet" })}
      onOpenChange={(o) => !o && setShowSignoffBlockedDialog(false)}
    ><p>{signoffTooltip}</p></BlockingMessageDialog>
    </>
  );
};
